import axios from "axios";
import DOMPurify from "dompurify";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const WORDS_PER_MINUTE = 200;

export const stripHtml = (value = "") => String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

// The API returns publish dates as [year, month, day]; tolerate strings too.
const toDate = (date) => {
  const parsed = Array.isArray(date) ? new Date(date[0], date[1] - 1, date[2]) : new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatBlogDate = (date) => {
  const parsed = toDate(date);
  return parsed ? parsed.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
};

/** Maps the public blog payload (/all/blogs...) to the shape the public pages use. */
export const normalizeBlog = (blog, index = 0) => {
  const text = stripHtml(blog.content || blog.description || blog.excerpt || "");
  const words = text ? text.split(" ").length : 0;
  const tags = (typeof blog.tags === "string" ? blog.tags.split(",") : Array.isArray(blog.tags) ? blog.tags : [])
    .map((t) => String(t).trim())
    .filter(Boolean);
  const id = blog.blogId ?? blog.id ?? blog._id;
  const published = toDate(blog.date || blog.publish_date);

  return {
    key: id ?? index,
    id,
    slug: blog.slug || String(id),
    title: blog.title || "Untitled article",
    excerpt: stripHtml(blog.excerpt || blog.description || blog.content || "").slice(0, 220),
    html: blog.content || "",
    image: blog.imageUrl || blog.image_url || "",
    category: (blog.tag || tags[0] || "Real Estate").trim(),
    tags,
    author: blog.author || "All Home Desire",
    date: formatBlogDate(blog.date || blog.publish_date),
    isoDate: published ? published.toISOString() : "",
    readTime: blog.readTime || `${Math.max(1, Math.ceil(words / WORDS_PER_MINUTE))} min read`,
    metaTitle: blog.meta_title || "",
    metaDescription: blog.meta_description || "",
  };
};

/** Public list: POST /all/blogs (paged, newest first). */
export const listBlogs = async ({ page = 1, size = 9, keyword = "" } = {}) => {
  const { data } = await axios.post(`${BASE_URL}/all/blogs`, {
    page_number: page,
    page_size: size,
    order_by: "DESC",
    keyword_search: keyword,
  });
  if (data?.statusType !== "SUCCESS") throw new Error(data?.text || "Unable to load articles");
  return {
    blogs: (data.blogs || []).map(normalizeBlog),
    totalPages: data.totalPage || 1,
    totalRecords: data.totalRecord ?? (data.blogs || []).length,
  };
};

/** Public detail: GET /all/blogs/{id}. */
export const getBlogById = async (id) => {
  const { data } = await axios.get(`${BASE_URL}/all/blogs/${id}`);
  if (data?.statusType !== "SUCCESS" || !data.blog) throw new Error("not-found");
  return normalizeBlog(data.blog);
};

/**
 * The backend has no "by slug" endpoint, so an article is found through the public list.
 * `hintId` (from a link) skips the lookup.
 */
export const getBlogBySlug = async (slug, hintId) => {
  if (hintId) return getBlogById(hintId);
  if (/^\d+$/.test(slug)) return getBlogById(slug);
  const { blogs } = await listBlogs({ page: 1, size: 200 });
  const match = blogs.find((b) => b.slug === slug);
  if (!match) throw new Error("not-found");
  return getBlogById(match.id);
};

// Article HTML comes from the admin editor; it is sanitized before it is ever rendered.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("rel", "noopener noreferrer");
    if (node.getAttribute("href")?.startsWith("http")) node.setAttribute("target", "_blank");
  }
  if (node.tagName === "IMG") node.setAttribute("loading", "lazy");
});

export const sanitizeArticleHtml = (html) =>
  DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "form", "input", "button", "iframe", "object", "embed"],
    FORBID_ATTR: ["style", "onerror", "onclick"],
  });
